import { z } from 'zod';

/**
 * Spanish copy for validation errors.
 *
 * The shared schemas in `@kainos/validation` are the single source of truth for
 * the *rules*; their default messages come from Zod and are in English. Wording
 * is a presentation concern — the same convention the design tokens and the
 * enum labels follow — so the app translates them here instead of hardcoding
 * Spanish into a package the API also parses with.
 *
 * Only the issues a person can actually produce from a form are covered.
 * Anything else falls through to Zod's own message, which is a developer
 * problem by then.
 */
const spanishErrorMap: z.ZodErrorMap = (issue, context) => {
  switch (issue.code) {
    case z.ZodIssueCode.invalid_type:
      if (issue.received === 'undefined' || issue.received === 'null') {
        return { message: 'Este campo es obligatorio.' };
      }
      return { message: 'El valor no tiene el formato esperado.' };

    case z.ZodIssueCode.too_small:
      if (issue.type === 'string') {
        return issue.minimum === 1
          ? { message: 'Este campo es obligatorio.' }
          : { message: `Debe tener al menos ${issue.minimum} caracteres.` };
      }
      return { message: `El valor mínimo es ${issue.minimum}.` };

    case z.ZodIssueCode.too_big:
      if (issue.type === 'string') {
        return { message: `No puede superar los ${issue.maximum} caracteres.` };
      }
      return { message: `El valor máximo es ${issue.maximum}.` };

    case z.ZodIssueCode.invalid_string:
      if (issue.validation === 'email') return { message: 'Escribe un correo válido.' };
      return { message: 'El formato no es válido.' };

    case z.ZodIssueCode.invalid_enum_value:
      return { message: 'Selecciona una de las opciones disponibles.' };

    default:
      return { message: context.defaultError };
  }
};

/**
 * Installs the Spanish messages process-wide.
 *
 * Called once from the app entry point. It is a global on purpose: every form
 * should read the same, and a per-form error map is a rule waiting to drift.
 */
export function installSpanishValidationMessages(): void {
  z.setErrorMap(spanishErrorMap);
}
